import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const SESSION_KEY = 'saniq_newsletter_popup_seen';
const DELAY_MS = 20000;

// Site-wide, once-per-session newsletter popup, distinct from the
// "Register for Updates" waitlist modal triggered from the nav/CTAs.
const PopupForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const timer = setTimeout(() => {
      setIsOpen(true);
      sessionStorage.setItem(SESSION_KEY, '1');
    }, DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          access_key: '36bd61b8-e929-4fd7-ae6f-049dd98ecde2',
          subject: 'New Newsletter Signup from SaniQ',
          from_name: 'SaniQ Newsletter Popup',
          name: email.split('@')[0] || 'Subscriber',
          email,
          replyto: email,
          message: `New newsletter signup: ${email}`,
          'Email Address': email,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Submission failed');
      setStatus('success');
    } catch (err) {
      console.error('Newsletter popup submission failed', err);
      setStatus('error');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          className="fixed bottom-6 right-6 z-[190] w-[calc(100%-3rem)] max-w-sm glass bg-[#0a0a0f] border border-white/10 rounded-2xl p-6 shadow-2xl"
        >
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            aria-label="Close newsletter signup"
          >
            <SafeIcon name="X" className="text-lg" />
          </button>

          {status === 'success' ? (
            <div className="flex items-center gap-3 py-2">
              <SafeIcon name="CheckCircle" className="text-cyan-400 text-2xl shrink-0" />
              <p className="text-white text-sm">You're subscribed. Thanks for reading.</p>
            </div>
          ) : (
            <>
              <p className="text-white text-sm font-medium mb-1 pr-6">Get hygiene insights + launch updates</p>
              <p className="text-white/40 text-xs mb-4">One email, roughly monthly. No spam.</p>
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === 'submitting'}
                  placeholder="you@email.com"
                  className="flex-1 min-w-0 rounded-full border border-white/10 bg-black/40 px-4 py-2.5 text-xs text-white focus:border-cyan-500 focus:outline-none disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="px-4 py-2.5 bg-cyan-500 text-black text-[10px] font-bold uppercase tracking-widest rounded-full hover:bg-cyan-400 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  Subscribe
                </button>
              </form>
              {status === 'error' && (
                <p className="text-red-400 text-[11px] mt-2">Something went wrong. Please try again.</p>
              )}
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PopupForm;
